import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import { callDutyKeys } from '@/lib/queryKeys'

export interface CallDutyOption {
  id: string
  name: string
  status: 'active' | 'inactive' | 'archived' | null
}

interface CallDutyOptionsResponse {
  data: CallDutyOption[]
}

async function fetchCallDutyOptions(token: string): Promise<CallDutyOption[]> {
  const response = await fetch(`${AGENT_URL}/data/calls/duty-options`, {
    headers: { Authorization: token },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch duty options: ${response.status}`)
  }

  const payload = (await response.json()) as CallDutyOptionsResponse
  return payload.data
}

export function useCallDutyOptions(enabled = true) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: callDutyKeys.all(organization?.id),
    enabled: enabled && !!organization?.id,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchCallDutyOptions(token)
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
